/**
 * KAN-845: шаги экрана "Начало работы" и память "Готово".
 * Данные берутся из тех же хуков, что у профиля и блока уведомлений:
 * useInstallPrompt, useMyManagerProfile, usePushSubscription,
 * useMyNotificationsStatus. Компоненты шагов только рисуют.
 *
 * ready: всё нужное загружено (до этого экран сам не открывается).
 * shouldAutoOpen: менеджер, не нажимал "Готово", есть несделанный обязательный шаг.
 * KAN-846: шаг "Логин и пароль" по needs_credentials профиля; заданный на этом
 * экране остаётся галочкой (needs_credentials уже false).
 * Вход без пароля держит экран открытым даже после "Готово": без пароля
 * менеджер не войдёт снова, когда ссылка и сессия кончатся.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useInstallPrompt } from '@/hooks/useInstallPrompt';
import { useMyManagerProfile } from '@/hooks/useManagerProfile';
import { useMyNotificationsStatus, usePushSubscription } from '@/hooks/usePushSubscription';
import {
  buildOnboardingSteps,
  type CredentialsState,
  dismissOnboarding,
  isOnboardingDismissed,
  requiredStepsDone,
} from '@/lib/managerOnboarding';

export function useOnboardingSteps() {
  const { data: user } = useCurrentUser();
  const managerId = user?.manager_id ?? null;
  const install = useInstallPrompt();
  const profile = useMyManagerProfile();
  const push = usePushSubscription();
  const notifications = useMyNotificationsStatus();
  const [dismissed, setDismissed] = useState(() => (managerId ? isOnboardingDismissed(managerId) : false));

  const needsCredentials = profile.data?.needs_credentials === true;
  const [credentialsAsked, setCredentialsAsked] = useState(needsCredentials);
  useEffect(() => {
    if (needsCredentials) setCredentialsAsked(true);
  }, [needsCredentials]);
  const credentials: CredentialsState = needsCredentials ? 'needed' : credentialsAsked ? 'done' : 'none';

  const status = notifications.data;
  const steps = useMemo(
    () =>
      buildOnboardingSteps({
        credentials,
        installKind: install.kind,
        phone: profile.data?.phone ?? '',
        workHours: profile.data?.work_hours,
        pushView: push.view,
        telegram: status ? { linked: status.telegram_linked, botConnected: status.bot_connected } : null,
      }),
    [credentials, install.kind, profile.data?.phone, profile.data?.work_hours, push.view, status]
  );

  const ready =
    !!managerId && !!profile.data && (!!status || notifications.isError) && push.view !== 'loading';
  const allDone = requiredStepsDone(steps);
  const isDismissed = dismissed || (!!managerId && isOnboardingDismissed(managerId));

  const dismiss = useCallback(() => {
    if (managerId) dismissOnboarding(managerId);
    setDismissed(true);
  }, [managerId]);

  return {
    hasManager: !!managerId,
    ready,
    steps,
    allDone,
    dismissed: isDismissed,
    needsCredentials,
    shouldAutoOpen: ready && !allDone && (!isDismissed || needsCredentials),
    dismiss,
    install,
    push,
    notificationsStatus: status,
  };
}
